import { useCallback, useEffect, useState } from "react";
import { emitKotFormDirty, emitKotFormReset, emitKotFormSaved } from "./kotDirtyEvents";

// Single owner of a KOT form's dirty flag. Every transition mirrors to the
// global unsaved-changes tracker (AddClient's kot-form-* listeners) so the
// Save/Cancel buttons and the leave-confirmation guard can never disagree.
// Dirty signals raised elsewhere on the page (e.g. AddClient's assignment
// modal, or its document-level input listeners) mirror back in via the
// kot-form-dirty event, since only one KOT section is mounted at a time.
export const useKotFormDirty = () => {
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    const handleExternalDirty = () => setIsDirty(true);
    window.addEventListener("kot-form-dirty", handleExternalDirty);
    return () => window.removeEventListener("kot-form-dirty", handleExternalDirty);
  }, []);

  const markDirty = useCallback(() => {
    setIsDirty(true);
    emitKotFormDirty();
  }, []);

  // The form matches server state again (save succeeded or found nothing to save).
  const markSaved = useCallback(() => {
    setIsDirty(false);
    emitKotFormSaved();
  }, []);

  // Local edits were discarded (Cancel).
  const markReset = useCallback(() => {
    setIsDirty(false);
    emitKotFormReset();
  }, []);

  // Local state was rebuilt from the store (mount, client switch, post-save
  // sync); the global side was already handled by whoever triggered the rebuild.
  const syncClean = useCallback(() => {
    setIsDirty(false);
  }, []);

  return { isDirty, markDirty, markSaved, markReset, syncClean };
};
